import ExpensesCodeModal from '@/components/program/ExpensesCodeModal';
import Footer from '@/components/program/Footer';
import { Header, HeaderWithSubtitle } from '@/components/program/Header';
import LocationModal from '@/components/program/LocationModal';
import RideTypeModal from '@/components/program/RideTypeModal';
import TimeModal from '@/components/program/TimeModal';
import { GROUPS } from '@/constants/group';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, type DialogProps } from 'primereact/dialog';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaCar, FaClock } from 'react-icons/fa';
import { MdPinDrop, MdReceiptLong } from 'react-icons/md';
import AddProgramForm from '../components/AddProgramForm';
import style from './program.module.css';

import { TRAVEL_RIDE_TYPE, TRAVEL_TIME_TYPE } from '@/constants/travel-program';
import { programInitialValues, programSchema } from '../engine/programs.modal';
import { useModal } from '@ebay/nice-modal-react';

interface Props { }

const AddProgramScreen = (props: Props) => {
	const { ...rest } = props;

	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [showExpensesCodeModal, setShowExpensesCodeModal] = useState(false);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [showTimeModal, setShowTimeCodeModal] = useState(false);
	const [showRideTypeModal, setShowRideTypeModal] = useState(false);

	const modal = useModal();

	// TODO: This is a temporary integration, remove or
	// refactor once react-hook-form is implemented.
	const methods = useForm({
		defaultValues: programInitialValues,
		resolver: yupResolver(programSchema),
	});

	const { watch } = methods;
	const locationTypeValue = watch('locationType');
	const expenseCodeTypeValue = watch('expenseCodeType');
	const rideTypeValue = watch('rideType');
	const ridesValue = watch('rides');
	const timeTypeValue = watch('timeType');

	const renderTimeRuleValue = () => {
		if (timeTypeValue === TRAVEL_TIME_TYPE.custom) return 'Custom time';
		if (timeTypeValue === TRAVEL_TIME_TYPE['all-day']) return 'All day (24 hours)';
		return 'Weekdays';
	};

	const rules = [
		{
			icon: <MdReceiptLong className="text-primary" size={24} />,
			title: 'Full coverage of trip expenses.',
			value: expenseCodeTypeValue === 'required'
				? 'Expense code required.'
				: 'No expense code required.',
			onClick: () => setShowExpensesCodeModal(true),
		},
		{
			icon: <MdPinDrop className="text-primary" size={24} />,
			title: 'Locations',
			value: locationTypeValue === 'any'
				? 'Any location'
				: 'Limited locations',
			onClick: () => setShowLocationModal(true),
		},
		{
			icon: <FaClock className="text-primary" size={24} />,
			title: 'Time',
			value: renderTimeRuleValue(),
			onClick: () => setShowTimeCodeModal(true),
		},
		{
			icon: <FaCar className="text-primary" size={24} />,
			title: 'Ride type',
			value: rideTypeValue === TRAVEL_RIDE_TYPE.all
				? 'All rides'
				: `Limited Ride types (${ridesValue?.length} selected)`,
			onClick: () => setShowRideTypeModal(true),
		},
	];

	const lastIndex = 3;
	const notificationMessage = 'Program successfully created.';
	const onNext = () => setActiveIndex((prev) => prev + 1);
	const onBack = () => activeIndex === 0 ? modal.remove() : setActiveIndex((prev) => prev - 1);

	const headerTemplate =
		activeIndex === 0 ? (
			<Header
				title={'Create Program'}
				id={'back-add-program'}
				onBack={onBack}
			/>
		) : activeIndex === 1 ? (
			<Header
				title={'Select Program'}
				id={'back-add-program'}
				onBack={onBack}
				description="Select the program you want to proceed with:"
			/>
		) : (
			<HeaderWithSubtitle
				title={'Customize your program'}
				subTitle={'Travel Program'}
				description={'You can always change these settings later.'}
				id={'back-add-program'}
				onBack={onBack}
			/>
		);

	const footerTemplate = (
		<Footer
			activeIndex={activeIndex}
			onNext={onNext}
			onSubmit={() => {
				modal.resolve({});
				modal.remove();
				showNotification(notificationMessage);
			}}
			lastIndex={lastIndex}
			id={activeIndex === lastIndex ? 'add-program' : 'back-add-program'}
		/>
	);

	return (
		<FormProvider {...methods}>
			<Dialog
				{...rest}
				header={headerTemplate}
				footer={footerTemplate}
				visible={true}
				onHide={() => {
					modal.remove();
				}}
				closable={false}
				draggable={false}
				className="md:max-w-[1136px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)]"
				pt={{
					root: {
						className: classNames(
							style['program-add-modal-top'],
							style['program-add-modal-bottom'],
							style['program-add-modal-root'],
						),
					},
					header: {
						className: classNames(
							style['program-add-modal-header'],
							style['program-add-modal-top'],
						),
					},
					content: {
						className: classNames(
							style['program-add-modal-content'],
						),
					},
					footer: {
						className: classNames(
							style['program-add-modal-bottom'],
							style['program-add-modal-footer'],
						),
					},
				}}
			>
				<AddProgramForm
					activeIndex={activeIndex}
					groups={GROUPS}
					rules={rules}
				/>
			</Dialog>
			<ExpensesCodeModal
				visible={showExpensesCodeModal}
				setVisible={setShowExpensesCodeModal}
				onHide={() => setShowExpensesCodeModal(false)}
			/>
			<LocationModal
				visible={showLocationModal}
				setVisible={setShowLocationModal}
				onHide={() => setShowLocationModal(false)}
			/>
			<TimeModal
				visible={showTimeModal}
				setVisible={setShowTimeCodeModal}
				onHide={() => setShowTimeCodeModal(false)}
			/>
			<RideTypeModal
				visible={showRideTypeModal}
				setVisible={setShowRideTypeModal}
				onHide={() => setShowRideTypeModal(false)}
			/>
		</FormProvider>
	);
};

export default AddProgramScreen;
